class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.headerSection = document.getElementById('shopify-section-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onCartLinkClick = this.handleCartLinkClick.bind(this);
    this.onDrawerActionClick = this.handleDrawerActionClick.bind(this);
    this.onCartPreferenceChange = this.handleCartPreferenceChange.bind(this);
    this.onCartPreferenceInput = this.handleCartPreferenceInput.bind(this);
    this.onCheckoutSubmit = this.handleCheckoutSubmit.bind(this);
    this.recommendationSequence = 0;
    this.preferenceSaveTimer = null;
    this.pendingPreferencePayload = null;
    this.preferenceSaveChain = Promise.resolve();
    this.preferenceSaveFailed = false;
    this.checkoutSubmitReady = false;
    this.openAnimationFrame = null;
    this.focusDrawerTimer = null;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('[data-cart-notification-overlay]')?.addEventListener('click', this.close.bind(this));
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    document.addEventListener('click', this.onCartLinkClick);
    this.addEventListener('click', this.onDrawerActionClick);
    this.addEventListener('change', this.onCartPreferenceChange);
    this.addEventListener('input', this.onCartPreferenceInput);
    this.querySelector('[data-cart-drawer-checkout-form]')?.addEventListener('submit', this.onCheckoutSubmit);
  }

  open() {
    const isAlreadyOpen = this.notification.classList.contains('active');

    this.notification.classList.add('animate');
    document.body.classList.add('smk-cart-drawer-open');
    if (this.headerSection) this.headerSection.classList.add('cart-notification-layer-active');

    const focusDrawer = () => {
      window.clearTimeout(this.focusDrawerTimer);
      this.notification.focus();
      trapFocus(this.notification);
    };

    if (isAlreadyOpen) {
      this.classList.add('is-open');
      focusDrawer();
    } else {
      // Match the mobile Menu: establish the closed state first, then begin
      // the eased motion on the next painted frame.
      this.openAnimationFrame = window.requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.notification.classList.add('active');
        this.openAnimationFrame = null;
      });

      const onDrawerTransitionEnd = (event) => {
        if (event.target !== this.notification || event.propertyName !== 'transform') return;
        this.notification.removeEventListener('transitionend', onDrawerTransitionEnd);
        if (this.notification.classList.contains('active')) focusDrawer();
      };
      this.notification.addEventListener('transitionend', onDrawerTransitionEnd);
      this.focusDrawerTimer = window.setTimeout(() => {
        this.notification.removeEventListener('transitionend', onDrawerTransitionEnd);
        if (this.notification.classList.contains('active')) focusDrawer();
      }, 900);
    }

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (this.openAnimationFrame) window.cancelAnimationFrame(this.openAnimationFrame);
    this.openAnimationFrame = null;
    window.clearTimeout(this.focusDrawerTimer);

    this.notification.classList.remove('active');
    this.classList.remove('is-open');
    document.body.classList.remove('smk-cart-drawer-open');
    if (this.headerSection) this.headerSection.classList.remove('cart-notification-layer-active');

    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section => {
      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    // The sticky header can still be an unupgraded element when this response
    // finishes. Cart feedback must not depend on that optional header method.
    if (this.header && typeof this.header.reveal === 'function') this.header.reveal();
    const cartHref = document.querySelector('#cart-icon-bubble')?.href || '/cart';
    this.openCart(cartHref, { preserveContents: true });
  }

  handleCartLinkClick(event) {
    const cartLink = event.target.closest('#cart-icon-bubble');
    if (!cartLink) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.setActiveElement(cartLink);
    this.openCart(cartLink.href);
  }

  async openCart(cartHref, options = {}) {
    const container = this.querySelector('#smk-cart-drawer-contents');
    if (!container) {
      window.location.assign(cartHref);
      return;
    }

    const preserveContents = options.preserveContents === true && container.innerHTML.trim() !== '';
    ++this.recommendationSequence;
    const recommendationContainer = this.querySelector('#smk-cart-drawer-recommendations');
    if (recommendationContainer && !preserveContents) recommendationContainer.hidden = true;

    this.notification.classList.add('is-cart-view');
    this.notification.classList.remove('is-cart-empty');
    container.hidden = false;
    const summary = this.querySelector('#smk-cart-drawer-summary');
    const shippingRegion = this.querySelector('[data-cart-shipping-region]');
    if (!preserveContents) {
      container.innerHTML = '<div class="smk-cart-drawer-contents__empty"><p>読み込み中…</p></div>';
      if (summary) summary.hidden = true;
      if (shippingRegion) shippingRegion.hidden = true;
    }
    this.open();

    const url = new URL(cartHref, window.location.origin);
    url.searchParams.set('section_id', 'smk-cart-drawer-contents');

    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Cart drawer request failed: ${response.status}`);

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const contents = parsedDocument.querySelector('[data-smk-cart-drawer-contents]');
      if (!contents) throw new Error('Cart drawer response did not contain contents');

      container.innerHTML = contents.innerHTML;
      const isEmpty = contents.dataset.cartEmpty === 'true';
      this.notification.classList.toggle('is-cart-empty', isEmpty);
      this.updateCartSummary(contents);
      this.setupCartPreferences(container);

      if (!isEmpty && contents.dataset.anchorProductId) {
        this.loadRecommendations(contents.dataset.anchorProductId, { preserveContents });
      } else if (recommendationContainer) {
        recommendationContainer.hidden = true;
        recommendationContainer.innerHTML = '';
      }

      const cartButton = this.querySelector('#cart-notification-button');
      if (cartButton) cartButton.hidden = true;
    } catch (error) {
      window.location.assign(cartHref);
    }
  }

  handleDrawerActionClick(event) {
    const recommendationToggle = event.target.closest('[data-cart-drawer-recommendations-toggle]');
    if (recommendationToggle) {
      event.preventDefault();
      this.toggleRecommendations(recommendationToggle);
      return;
    }

    const recommendationPrevious = event.target.closest('[data-cart-drawer-recommendations-prev]');
    const recommendationNext = event.target.closest('[data-cart-drawer-recommendations-next]');
    if (recommendationPrevious || recommendationNext) {
      event.preventDefault();
      this.scrollRecommendations(recommendationPrevious || recommendationNext, recommendationNext ? 1 : -1);
      return;
    }

    const quantityButton = event.target.closest('[data-cart-drawer-quantity]');
    if (quantityButton) {
      event.preventDefault();
      this.updateCartItemQuantity(quantityButton);
      return;
    }

    const removeButton = event.target.closest('[data-cart-drawer-remove]');
    if (!removeButton) return;

    event.preventDefault();
    this.removeCartItem(removeButton);
  }

  setupCartPreferences(container) {
    const preferences = container.querySelector('[data-cart-drawer-preferences]');
    if (!preferences) return;

    let shouldSave = preferences.dataset.storedShippingMethod !== this.getSelectedShippingMethod(preferences);
    const dateInput = preferences.querySelector('[data-cart-drawer-delivery-date]');
    if (dateInput) {
      const today = new Date();
      const minimumDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 6);
      const maximumDate = new Date(today.getFullYear(), today.getMonth() + 2, today.getDate());
      const minimumDateValue = this.formatLocalDate(minimumDate);
      const maximumDateValue = this.formatLocalDate(maximumDate);
      dateInput.min = minimumDateValue;
      dateInput.max = maximumDateValue;
      if (dateInput.value && (dateInput.value < minimumDateValue || dateInput.value > maximumDateValue)) {
        dateInput.value = '';
        shouldSave = true;
      }
    }

    if (this.updateShippingMethodFields(preferences, true)) shouldSave = true;
    this.updateDeliveryDateGuidance(preferences);
    this.updateCartPreferenceSummary(preferences);
    if (shouldSave) this.scheduleCartPreferencesSave(preferences, 0);
  }

  getSelectedShippingMethod(preferences) {
    return preferences.querySelector('[data-cart-drawer-shipping-method]:checked')?.value || 'ヤマト運輸';
  }

  updateShippingMethodFields(preferences, clearUnavailableFields = false) {
    const usesYamato = this.getSelectedShippingMethod(preferences) === 'ヤマト運輸';
    const dateField = preferences.querySelector('[data-cart-drawer-delivery-date-field]');
    const timeField = preferences.querySelector('[data-cart-drawer-delivery-time-field]');
    const dateInput = preferences.querySelector('[data-cart-drawer-delivery-date]');
    const timeSelect = preferences.querySelector('[data-cart-drawer-delivery-time]');
    const clickPostNotice = preferences.querySelector('[data-cart-drawer-click-post-notice]');
    const yamatoNotice = preferences.querySelector('[data-cart-drawer-yamato-notice]');
    let changed = false;

    if (dateField) dateField.hidden = !usesYamato;
    if (timeField) timeField.hidden = !usesYamato;
    if (dateInput) dateInput.disabled = !usesYamato;
    if (timeSelect) timeSelect.disabled = !usesYamato;
    if (clickPostNotice) clickPostNotice.hidden = usesYamato;
    if (yamatoNotice) yamatoNotice.hidden = !usesYamato;

    if (!usesYamato && clearUnavailableFields) {
      if (dateInput?.value) {
        dateInput.value = '';
        changed = true;
      }
      if (timeSelect && timeSelect.value !== '指定なし') {
        timeSelect.value = '指定なし';
        changed = true;
      }
    }

    return changed;
  }

  updateDeliveryDateGuidance(preferences) {
    const guidance = preferences.querySelector('[data-cart-drawer-date-guidance]');
    const dateInput = preferences.querySelector('[data-cart-drawer-delivery-date]');
    if (!guidance || !dateInput) return;

    const [year, month, day] = dateInput.min.split('-').map(Number);
    if (!year || !month || !day) return;
    const locale = document.documentElement.lang || 'ja';
    const dateLabel = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric' })
      .format(new Date(year, month - 1, day));
    guidance.textContent = (guidance.dataset.dateAvailableTemplate || '{date}').replace('{date}', dateLabel);
  }

  formatLocalDate(date) {
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  handleCartPreferenceChange(event) {
    const preferences = event.target.closest('[data-cart-drawer-preferences]');
    if (!preferences) return;

    if (
      event.target.matches('[data-cart-drawer-delivery-date]')
      && event.target.value
      && event.target.min
      && event.target.value < event.target.min
    ) {
      event.target.value = event.target.min;
    }

    if (event.target.matches('[data-cart-drawer-shipping-method]')) {
      this.updateShippingMethodFields(preferences, true);
    }

    if (event.target.matches('[data-cart-drawer-shipping-method], [data-cart-drawer-delivery-date], [data-cart-drawer-delivery-time], [data-cart-drawer-note]')) {
      this.updateCartPreferenceSummary(preferences);
      this.scheduleCartPreferencesSave(preferences, 0);
    }
  }

  handleCartPreferenceInput(event) {
    const preferences = event.target.closest('[data-cart-drawer-preferences]');
    if (!preferences || !event.target.matches('[data-cart-drawer-note]')) return;

    this.updateCartPreferenceSummary(preferences);
    this.scheduleCartPreferencesSave(preferences, 650);
  }

  updateCartPreferenceSummary(preferences) {
    const configuredLabel = preferences.querySelector('[data-cart-drawer-preferences-configured]');
    if (!configuredLabel) return;

    const payload = this.collectCartPreferences(preferences);
    const shippingMethod = payload.attributes['配送方法'];
    const deliveryDate = payload.attributes['配送希望日'];
    const deliveryTime = payload.attributes['配送希望時間帯'];
    let summary = shippingMethod === 'クリックポスト'
      ? preferences.dataset.clickPostText
      : preferences.dataset.yamatoText;

    if (shippingMethod === 'ヤマト運輸' && deliveryDate) {
      const [year, month, day] = deliveryDate.split('-').map(Number);
      if (year && month && day) {
        const locale = document.documentElement.lang || 'ja';
        const dateLabel = new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric' })
          .format(new Date(year, month - 1, day));
        summary += `・${dateLabel}`;
      }
      if (deliveryTime !== '指定なし') summary += `・${deliveryTime}`;
    } else if (shippingMethod === 'ヤマト運輸' && deliveryTime !== '指定なし') {
      summary += `・${deliveryTime}`;
    }

    configuredLabel.textContent = summary;
  }

  collectCartPreferences(preferences) {
    const shippingMethod = this.getSelectedShippingMethod(preferences);
    const usesYamato = shippingMethod === 'ヤマト運輸';
    const deliveryDate = usesYamato ? preferences.querySelector('[data-cart-drawer-delivery-date]')?.value || '' : '';
    const deliveryTime = usesYamato ? preferences.querySelector('[data-cart-drawer-delivery-time]')?.value || '指定なし' : '指定なし';
    const deliveryPreference = usesYamato && (deliveryDate || deliveryTime !== '指定なし') ? '指定する' : '指定しない';
    const note = preferences.querySelector('[data-cart-drawer-note]')?.value || '';

    return {
      attributes: {
        '配送方法': shippingMethod,
        '配送日指定の有無': deliveryPreference,
        '配送希望日': deliveryDate,
        '配送希望時間帯': deliveryTime
      },
      note
    };
  }

  scheduleCartPreferencesSave(preferences, delay) {
    this.pendingPreferencePayload = this.collectCartPreferences(preferences);
    clearTimeout(this.preferenceSaveTimer);

    const status = preferences.querySelector('[data-cart-drawer-save-status]');
    if (status) status.textContent = preferences.dataset.savingText;

    if (delay > 0) {
      this.preferenceSaveTimer = setTimeout(() => {
        this.preferenceSaveTimer = null;
        this.enqueueCartPreferencesSave(this.pendingPreferencePayload, preferences);
      }, delay);
      return;
    }

    this.preferenceSaveTimer = null;
    this.enqueueCartPreferencesSave(this.pendingPreferencePayload, preferences);
  }

  enqueueCartPreferencesSave(payload, preferences) {
    this.preferenceSaveChain = this.preferenceSaveChain
      .catch(() => {})
      .then(() => this.saveCartPreferences(payload, preferences));
    return this.preferenceSaveChain;
  }

  async saveCartPreferences(payload, preferences) {
    try {
      const response = await fetch(routes.cart_update_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error(`Cart preferences update failed: ${response.status}`);
      this.preferenceSaveFailed = false;
      const status = preferences.querySelector('[data-cart-drawer-save-status]');
      if (status) status.textContent = preferences.dataset.savedText;
      const fallbackLink = this.querySelector('#cart-notification-button');
      if (fallbackLink) fallbackLink.hidden = true;
    } catch (error) {
      this.preferenceSaveFailed = true;
      const status = preferences.querySelector('[data-cart-drawer-save-status]');
      if (status) status.textContent = preferences.dataset.errorText;
      const fallbackLink = this.querySelector('#cart-notification-button');
      if (fallbackLink) fallbackLink.hidden = false;
      throw error;
    }
  }

  async flushCartPreferences() {
    if (this.preferenceSaveTimer) {
      clearTimeout(this.preferenceSaveTimer);
      this.preferenceSaveTimer = null;
      const preferences = this.querySelector('[data-cart-drawer-preferences]');
      if (preferences && this.pendingPreferencePayload) {
        this.enqueueCartPreferencesSave(this.pendingPreferencePayload, preferences);
      }
    }

    try {
      await this.preferenceSaveChain;
    } catch (error) {
      // The inline message and fallback cart link explain how to continue.
    }

    return !this.preferenceSaveFailed;
  }

  async handleCheckoutSubmit(event) {
    if (this.checkoutSubmitReady) return;

    event.preventDefault();
    const submitter = event.submitter || event.currentTarget.querySelector('[name="checkout"]');
    if (submitter) submitter.disabled = true;

    const saved = await this.flushCartPreferences();
    if (!saved) {
      if (submitter) submitter.disabled = false;
      return;
    }

    this.checkoutSubmitReady = true;
    if (submitter) submitter.disabled = false;
    if (event.currentTarget.requestSubmit) {
      event.currentTarget.requestSubmit(submitter || undefined);
    } else {
      window.location.assign('/checkout');
    }
  }

  async removeCartItem(button) {
    const lineKey = button.dataset.lineKey;
    if (!lineKey || button.disabled) return;

    button.disabled = true;
    const cartLink = document.querySelector('#cart-icon-bubble')?.href || '/cart';

    try {
      const response = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: ['cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });
      if (!response.ok) throw new Error(`Cart removal failed: ${response.status}`);

      const state = await response.json();
      const cartIconHtml = state.sections && state.sections['cart-icon-bubble'];
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon && cartIconHtml) {
        cartIcon.innerHTML = this.getSectionInnerHTML(cartIconHtml);
      }

      await this.openCart(cartLink, { preserveContents: true });
    } catch (error) {
      button.disabled = false;
    }
  }

  async updateCartItemQuantity(button) {
    const lineKey = button.dataset.lineKey;
    const currentQuantity = Number(button.dataset.currentQuantity);
    const quantityChange = Number(button.dataset.quantityChange);
    const nextQuantity = currentQuantity + quantityChange;
    if (!lineKey || !Number.isFinite(nextQuantity) || nextQuantity < 1 || button.disabled) return;

    const control = button.closest('[data-cart-drawer-quantity-control]');
    const item = button.closest('.smk-cart-drawer-contents__item');
    const buttons = control?.querySelectorAll('[data-cart-drawer-quantity]') || [];
    buttons.forEach((quantityButton) => { quantityButton.disabled = true; });
    item?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: nextQuantity,
          sections: ['cart-icon-bubble', 'smk-cart-drawer-contents'],
          sections_url: window.location.pathname
        })
      });
      if (!response.ok) throw new Error(`Cart quantity update failed: ${response.status}`);

      const state = await response.json();
      const cartIconHtml = state.sections && state.sections['cart-icon-bubble'];
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon && cartIconHtml) cartIcon.innerHTML = this.getSectionInnerHTML(cartIconHtml);

      const contentsHtml = state.sections && state.sections['smk-cart-drawer-contents'];
      const container = this.querySelector('#smk-cart-drawer-contents');
      if (!container || !contentsHtml) throw new Error('Updated cart contents were not returned');

      const parsedDocument = new DOMParser().parseFromString(contentsHtml, 'text/html');
      const contents = parsedDocument.querySelector('[data-smk-cart-drawer-contents]');
      if (!contents) throw new Error('Updated cart contents could not be parsed');

      container.innerHTML = contents.innerHTML;
      this.notification.classList.toggle('is-cart-empty', contents.dataset.cartEmpty === 'true');
      this.updateCartSummary(contents);
      this.setupCartPreferences(container);
    } catch (error) {
      buttons.forEach((quantityButton) => {
        const change = Number(quantityButton.dataset.quantityChange);
        quantityButton.disabled = currentQuantity + change < 1;
      });
      item?.removeAttribute('aria-busy');
    }
  }

  updateCartSummary(contents) {
    const summary = this.querySelector('#smk-cart-drawer-summary');
    const subtotal = summary?.querySelector('[data-cart-drawer-subtotal]');
    if (summary && subtotal) {
      subtotal.textContent = contents.dataset.cartTotal || '';
      summary.hidden = contents.dataset.cartEmpty === 'true';
    }

    const shippingRegion = this.querySelector('[data-cart-shipping-region]');
    const shippingMessage = this.querySelector('#cart-free-shipping-message');
    if (shippingRegion && shippingMessage) {
      shippingMessage.textContent = contents.dataset.freeShippingMessage || '';
      shippingRegion.hidden = shippingMessage.textContent.trim() === '';
    }
  }

  async loadCartSummary() {
    const cartLink = document.querySelector('#cart-icon-bubble')?.href || '/cart';
    const url = new URL(cartLink, window.location.origin);
    url.searchParams.set('section_id', 'smk-cart-drawer-contents');

    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) return;
      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const contents = parsedDocument.querySelector('[data-smk-cart-drawer-contents]');
      if (contents) this.updateCartSummary(contents);
    } catch (error) {
      // The cart confirmation remains usable if the summary cannot load.
    }
  }

  async loadRecommendations(productId, options = {}) {
    const container = this.querySelector('#smk-cart-drawer-recommendations');
    if (!container) return;

    const preserveContents = options.preserveContents === true && container.innerHTML.trim() !== '';
    const sequence = ++this.recommendationSequence;
    if (!preserveContents) {
      container.hidden = true;
      container.innerHTML = '';
    }
    container.setAttribute('aria-busy', 'true');
    if (!productId) {
      container.removeAttribute('aria-busy');
      return;
    }

    const recommendationsUrl = container.dataset.recommendationsUrl || '/recommendations/products';
    const url = new URL(recommendationsUrl, window.location.origin);
    url.searchParams.set('section_id', 'smk-cart-drawer-recommendations');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', 'related');

    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
      const html = await response.text();
      if (sequence !== this.recommendationSequence) return;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const recommendations = parsedDocument.querySelector('[data-cart-drawer-recommendations]');
      if (!recommendations || !recommendations.children.length) return;

      container.innerHTML = recommendations.innerHTML;
      container.hidden = false;
      this.setupRecommendationSlider(container);
    } catch (error) {
      // Recommendations are optional and must never interrupt the cart flow.
    } finally {
      if (sequence === this.recommendationSequence) container.removeAttribute('aria-busy');
    }
  }

  setupRecommendationSlider(container) {
    const track = container.querySelector('.smk-cart-drawer-recommendations__track');
    const items = track?.querySelectorAll('.smk-cart-drawer-recommendations__item');
    const navigation = container.querySelector('.smk-cart-drawer-recommendations__nav');
    const progress = container.querySelector('[data-cart-drawer-recommendations-progress]');
    if (!track || !items?.length || !navigation || !progress) return;

    navigation.hidden = items.length <= 1;
    progress.style.width = `${100 / items.length}%`;

    const update = () => {
      const lastIndex = items.length - 1;
      const itemWidth = track.clientWidth || 1;
      const activeIndex = Math.min(lastIndex, Math.max(0, Math.round(track.scrollLeft / itemWidth)));
      progress.style.transform = `translateX(${activeIndex * 100}%)`;

      const previous = navigation.querySelector('[data-cart-drawer-recommendations-prev]');
      const next = navigation.querySelector('[data-cart-drawer-recommendations-next]');
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === lastIndex;
    };

    track.addEventListener('scroll', update, { passive: true });
    update();
  }

  scrollRecommendations(button, direction) {
    const container = button.closest('#smk-cart-drawer-recommendations');
    const track = container?.querySelector('.smk-cart-drawer-recommendations__track');
    const items = track?.querySelectorAll('.smk-cart-drawer-recommendations__item');
    if (!track || !items?.length) return;

    const lastIndex = items.length - 1;
    const activeIndex = Math.round(track.scrollLeft / (track.clientWidth || 1));
    const nextIndex = Math.min(lastIndex, Math.max(0, activeIndex + direction));
    track.scrollTo({ left: nextIndex * track.clientWidth, behavior: 'smooth' });
  }

  toggleRecommendations(button) {
    const container = button.closest('#smk-cart-drawer-recommendations');
    if (!container) return;

    const isCollapsed = container.classList.toggle('is-collapsed');
    button.setAttribute('aria-expanded', String(!isCollapsed));
    button.setAttribute('aria-label', isCollapsed ? button.dataset.expandLabel : button.dataset.collapseLabel);
    const symbol = button.querySelector('[aria-hidden="true"]');
    if (symbol) symbol.textContent = isCollapsed ? '+' : '−';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button'
      },
      {
        id: 'cart-icon-bubble'
      },
      {
        id: 'cart-free-shipping-message',
        selector: '#cart-free-shipping-message'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
